/**
 * Admin – Nutzerverwaltung
 *
 * Features:
 *   - "UNVERIFIZIERTE ACCOUNTS ÄLTER ALS 7 TAGE"-Liste (NT-REG-FUNNEL-01, Teil F)
 *   - Stats-Kacheln: Gesamt / Verifiziert / Ausstehend / Gesperrt
 *   - Suchfeld + Filter-Buttons
 *   - Paginierte User-Tabelle mit Aktionen
 *   - Bestätigungs-Modal vor destruktiven Aktionen
 *   - Toast-Benachrichtigungen
 *
 * Routen (Backend):
 *   GET  /api/v1/admin/users
 *   GET  /api/v1/admin/users/unverified-stale
 *   POST /api/v1/admin/users/:id/resend-verification
 *   POST /api/v1/admin/users/:id/generate-token
 *   POST /api/v1/admin/users/:id/force-verify
 *   POST /api/v1/admin/users/:id/delete-unverified
 *   POST /api/v1/admin/users/:id/ban
 *   POST /api/v1/admin/users/:id/unban
 *   POST /api/v1/admin/users/:id/grant-alpha-badge
 */

import { api, ApiError } from '../../services/api.js';
import { calcNoticeDisplayMs } from '../../utils/notice-duration.js';

// ─── Interfaces ───────────────────────────────────────────────────────────────

type AlphaTesterGrantMethod = 'AUTO_LEVEL' | 'ADMIN_GRANTED';

interface AdminUserProfile {
  username: string;
  level: number;
  isAlphaTester: boolean;
  alphaTesterGrantMethod: AlphaTesterGrantMethod | null;
  lastSeenAt: string | null;
}

interface AdminUser {
  id: string;
  role: string;
  isVerified: boolean;
  isBanned: boolean;
  createdAt: string;
  deletedAt: string | null;
  profile: AdminUserProfile | null;
}

interface UsersMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
  timestamp: string;
}

interface StaleUnverifiedUser {
  id: string;
  createdAt: string;
  profile: { username: string } | null;
}

type StatusFilter = 'all' | 'verified' | 'unverified' | 'banned';

// ─── Modulzustand ─────────────────────────────────────────────────────────────

let containerId = '';
let currentFilter: StatusFilter = 'all';
let currentSearch = '';
let currentPage = 1;
let pendingConfirmCallback: (() => void) | null = null;
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let toastTimer: ReturnType<typeof setTimeout> | null = null;

// ─── Einstiegspunkt ───────────────────────────────────────────────────────────

export function initAdminUsers(targetContainerId: string): void {
  containerId = targetContainerId;
  currentFilter = 'all';
  currentSearch = '';
  currentPage = 1;
  pendingConfirmCallback = null;
  renderSkeleton();
  void loadUsers();
  void loadStaleUnverified();
}

// ─── Grundstruktur ────────────────────────────────────────────────────────────

function renderSkeleton(): void {
  const container = document.getElementById(containerId);
  if (!container) return;

  container.innerHTML = `
    <div id="au-stale-section"
         style="margin-bottom:20px;border:1px solid var(--accent-gold);padding:12px;">
      <div style="display:flex;justify-content:space-between;align-items:center;
                  margin-bottom:8px;flex-wrap:wrap;gap:8px;">
        <span style="color:var(--accent-gold);font-size:12px;letter-spacing:0.08em;">
          UNVERIFIZIERTE ACCOUNTS ÄLTER ALS 7 TAGE
        </span>
        <button id="au-stale-refresh"
                style="border:1px solid var(--border);color:var(--text-muted);
                       background:transparent;padding:3px 10px;cursor:pointer;
                       font-family:'Share Tech Mono',monospace;font-size:11px;">
          AKTUALISIEREN
        </button>
      </div>
      <div id="au-stale-list">
        <p style="color:var(--text-muted);font-size:12px;">LADE...</p>
      </div>
    </div>

    <div id="au-stats"
         style="display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:16px;">
      ${statTileHtml('GESAMT',      '–', 'var(--text-primary)')}
      ${statTileHtml('VERIFIZIERT', '–', 'var(--accent-green)')}
      ${statTileHtml('AUSSTEHEND',  '–', 'var(--accent-gold)')}
      ${statTileHtml('GESPERRT',    '–', 'var(--accent-red)')}
    </div>

    <div style="display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap;align-items:center;">
      <input
        id="au-search"
        type="text"
        placeholder="USERNAME SUCHEN..."
        value="${escapeAttr(currentSearch)}"
        style="flex:1;min-width:160px;padding:8px 12px;
               background:var(--bg-elevated);border:1px solid var(--border);
               color:var(--text-primary);font-family:'Share Tech Mono',monospace;
               font-size:13px;outline:none;border-radius:0;"
      />
      <div id="au-filters" style="display:flex;gap:4px;flex-wrap:wrap;">
        ${filterBtnHtml('all',        'ALLE')}
        ${filterBtnHtml('unverified', 'NICHT VERIFIZIERT')}
        ${filterBtnHtml('verified',   'VERIFIZIERT')}
        ${filterBtnHtml('banned',     'GESPERRT')}
      </div>
    </div>

    <div id="au-table-wrap" style="overflow-x:auto;">
      <p style="color:var(--text-muted);">LADE NUTZER...</p>
    </div>

    <div id="au-pagination"
         style="display:flex;gap:6px;justify-content:flex-end;
                margin-top:12px;align-items:center;flex-wrap:wrap;">
    </div>

    <!-- Bestätigungs-Modal (Shell = .modal-backdrop/.modal aus dashboard.css,
         siehe components/BottomSheet.ts). Klasse "modal-backdrop" wird bewusst NICHT hier im
         Markup gesetzt, sondern erst in showConfirmModal()/closeModal() dynamisch an/aus —
         main.ts' globaler Scroll-Lock-MutationObserver prüft nur auf Präsenz der Klasse
         "modal-backdrop" im DOM, nicht auf Sichtbarkeit. Ein dauerhaft im DOM stehendes,
         nur per display:none verstecktes Element mit dieser Klasse hätte den ganzen
         Admin-Dashboard-Body permanent unscrollbar gemacht, sobald der NUTZER-Tab einmal
         gerendert wurde (Chris-Bug-Report 2026-07-30). -->
    <div id="au-modal-overlay" style="display:none;">
      <div class="modal modal--compact" role="dialog" aria-modal="true" aria-label="Bestätigung">
        <div class="modal-body">
          <p id="au-modal-text"
             style="color:var(--text-primary);margin:0;line-height:1.6;"></p>
        </div>
        <div class="modal-actions">
          <button id="au-modal-cancel"
                  style="border:1px solid var(--border);color:var(--text-muted);
                         background:transparent;padding:6px 16px;cursor:pointer;
                         font-family:inherit;font-size:13px;">
            ABBRECHEN
          </button>
          <button id="au-modal-confirm"
                  style="border:1px solid var(--accent-red);color:var(--accent-red);
                         background:transparent;padding:6px 16px;cursor:pointer;
                         font-family:inherit;font-size:13px;">
            BESTÄTIGEN
          </button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div id="au-toast" hidden
         style="position:fixed;bottom:80px;right:24px;
                padding:12px 18px;font-family:'Share Tech Mono',monospace;
                font-size:13px;border:1px solid var(--border);z-index:2000;
                max-width:340px;line-height:1.5;">
    </div>
  `;

  attachSkeletonHandlers();
}

function statTileHtml(label: string, value: string, color: string): string {
  return `
    <div style="background:var(--bg-surface);border:1px solid var(--border);
                padding:12px;text-align:center;">
      <div style="color:var(--text-muted);font-size:11px;
                  letter-spacing:0.1em;margin-bottom:4px;">${label}</div>
      <div style="color:${color};font-size:20px;
                  font-family:'Share Tech Mono',monospace;font-weight:bold;">${value}</div>
    </div>
  `;
}

function filterBtnHtml(filter: StatusFilter, label: string): string {
  const active = filter === currentFilter;
  return `
    <button
      data-au-filter="${filter}"
      style="border:1px solid ${active ? 'var(--accent-green)' : 'var(--border)'};
             color:${active ? 'var(--accent-green)' : 'var(--text-muted)'};
             background:transparent;padding:6px 12px;cursor:pointer;
             font-family:'Share Tech Mono',monospace;font-size:12px;
             letter-spacing:0.05em;white-space:nowrap;">
      ${label}
    </button>
  `;
}

// ─── Handler (Skeleton-Ebene) ─────────────────────────────────────────────────

function attachSkeletonHandlers(): void {
  // Suchfeld mit Debounce
  const searchInput = document.getElementById('au-search') as HTMLInputElement | null;
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      if (searchDebounceTimer !== null) clearTimeout(searchDebounceTimer);
      searchDebounceTimer = setTimeout(() => {
        currentSearch = searchInput.value.trim();
        currentPage = 1;
        void loadUsers();
      }, 350);
    });
  }

  // Filter-Buttons
  const filtersEl = document.getElementById('au-filters');
  if (filtersEl) {
    filtersEl.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-au-filter]');
      if (!btn) return;
      const filter = btn.getAttribute('data-au-filter') as StatusFilter;
      if (filter === currentFilter) return;
      currentFilter = filter;
      currentPage = 1;
      filtersEl.innerHTML =
        filterBtnHtml('all',        'ALLE') +
        filterBtnHtml('unverified', 'NICHT VERIFIZIERT') +
        filterBtnHtml('verified',   'VERIFIZIERT') +
        filterBtnHtml('banned',     'GESPERRT');
      void loadUsers();
    });
  }

  // Stale-Liste
  document.getElementById('au-stale-refresh')?.addEventListener('click', () => {
    void loadStaleUnverified();
  });
  document.getElementById('au-stale-list')?.addEventListener('click', handleStaleListClick);

  // Modal
  document.getElementById('au-modal-cancel')?.addEventListener('click', closeModal);
  document.getElementById('au-modal-confirm')?.addEventListener('click', () => {
    if (pendingConfirmCallback) {
      const cb = pendingConfirmCallback;
      closeModal();
      cb();
    }
  });
  document.getElementById('au-modal-overlay')?.addEventListener('click', (e) => {
    if (e.target === e.currentTarget) closeModal();
  });
}

// ─── Daten laden ──────────────────────────────────────────────────────────────

async function loadUsers(): Promise<void> {
  const tableWrap = document.getElementById('au-table-wrap');
  if (tableWrap) tableWrap.innerHTML = `<p style="color:var(--text-muted);">LADE NUTZER...</p>`;

  const params = new URLSearchParams({
    status: currentFilter,
    page:   String(currentPage),
    limit:  '50',
  });
  if (currentSearch) params.set('search', currentSearch);

  try {
    // getRaw() statt get(): braucht die echten meta-Felder (total/page/pages), die das normale
    // get() beim automatischen Entpacken der { data, meta }-Hülle verwerfen würde.
    const result = await api.getRaw<AdminUser[]>(`/admin/users?${params.toString()}`);
    renderTable(result.data);
    renderPagination(result.meta as unknown as UsersMeta);
    void loadStats();
  } catch (err) {
    if (tableWrap) {
      const msg = err instanceof ApiError ? err.message : 'Fehler beim Laden der Nutzer.';
      tableWrap.innerHTML = `<p style="color:var(--accent-red);">${escapeHtml(msg)}</p>`;
    }
  }
}

// Stats separat laden um Tabelle nicht zu blockieren
async function loadStats(): Promise<void> {
  const statsEl = document.getElementById('au-stats');
  if (!statsEl) return;

  try {
    const [allRes, verifiedRes, unverifiedRes, bannedRes] = await Promise.all([
      api.getRaw<AdminUser[]>('/admin/users?status=all&limit=1'),
      api.getRaw<AdminUser[]>('/admin/users?status=verified&limit=1'),
      api.getRaw<AdminUser[]>('/admin/users?status=unverified&limit=1'),
      api.getRaw<AdminUser[]>('/admin/users?status=banned&limit=1'),
    ]);
    const total = (m: Record<string, unknown>): string => String((m as unknown as UsersMeta).total);
    statsEl.innerHTML =
      statTileHtml('GESAMT',      total(allRes.meta),       'var(--text-primary)') +
      statTileHtml('VERIFIZIERT', total(verifiedRes.meta),  'var(--accent-green)') +
      statTileHtml('AUSSTEHEND',  total(unverifiedRes.meta),'var(--accent-gold)') +
      statTileHtml('GESPERRT',    total(bannedRes.meta),     'var(--accent-red)');
  } catch {
    // Stats-Fehler nicht blockierend anzeigen
  }
}

// ─── "Unverifizierte Accounts älter als 7 Tage"-Liste (NT-REG-FUNNEL-01) ──────

async function loadStaleUnverified(): Promise<void> {
  const listEl = document.getElementById('au-stale-list');
  if (!listEl) return;

  try {
    const users = await api.get<StaleUnverifiedUser[]>('/admin/users/unverified-stale');
    renderStaleList(users);
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : 'Fehler beim Laden.';
    listEl.innerHTML = `<p style="color:var(--accent-red);font-size:12px;">${escapeHtml(msg)}</p>`;
  }
}

function renderStaleList(users: StaleUnverifiedUser[]): void {
  const listEl = document.getElementById('au-stale-list');
  if (!listEl) return;

  if (users.length === 0) {
    listEl.innerHTML = `<p style="color:var(--text-muted);font-size:12px;">Keine liegengebliebenen Accounts.</p>`;
    return;
  }

  listEl.innerHTML = users.map((u) => {
    const username = escapeHtml(u.profile?.username ?? '[kein Profil]');
    const createdAt = new Date(u.createdAt).toLocaleDateString('de-DE');
    return `
      <div data-stale-user-id="${u.id}"
           style="display:flex;justify-content:space-between;align-items:center;
                  gap:8px;flex-wrap:wrap;padding:6px 0;border-bottom:1px solid #1a1a22;">
        <span style="color:var(--text-primary);font-size:12px;">
          ${username} <span style="color:var(--text-muted);">— registriert ${createdAt}</span>
        </span>
        <span>
          ${actionBtn('stale-resend', u.id, 'LINK SENDEN', 'var(--accent-blue)')}
          ${actionBtn('stale-delete', u.id, 'LÖSCHEN', 'var(--accent-red)')}
        </span>
      </div>
    `;
  }).join('');
}

function handleStaleListClick(e: Event): void {
  const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-au-action]');
  if (!btn) return;

  const action = btn.getAttribute('data-au-action');
  const userId = btn.getAttribute('data-au-user-id');
  if (!action || !userId) return;

  if (action === 'stale-resend') {
    void handleResend(userId, { refreshStale: true });
  } else if (action === 'stale-delete') {
    showConfirmModal(
      'Diesen unverifizierten Account endgültig löschen und anonymisieren? Das kann nicht rückgängig gemacht werden.',
      () => void handleDeleteUnverified(userId),
    );
  }
}

async function handleDeleteUnverified(userId: string): Promise<void> {
  try {
    await api.post<{ message: string }>(`/admin/users/${userId}/delete-unverified`, {});
    showToast('Account gelöscht und anonymisiert.', 'success');
    void loadUsers();
    void loadStaleUnverified();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Löschen.', 'error');
  }
}

// ─── Tabelle rendern ──────────────────────────────────────────────────────────

function renderTable(users: AdminUser[]): void {
  const tableWrap = document.getElementById('au-table-wrap');
  if (!tableWrap) return;

  if (users.length === 0) {
    tableWrap.innerHTML = `<p style="color:var(--text-muted);">KEINE NUTZER GEFUNDEN.</p>`;
    return;
  }

  tableWrap.innerHTML = `
    <table style="width:100%;border-collapse:collapse;
                  font-family:'Share Tech Mono',monospace;font-size:13px;">
      <thead>
        <tr style="color:var(--accent-gold);text-align:left;border-bottom:1px solid var(--border);">
          <th style="padding:8px 12px;">USERNAME</th>
          <th style="padding:8px 12px;">STATUS</th>
          <th style="padding:8px 12px;">REGISTRIERT</th>
          <th style="padding:8px 12px;">ZULETZT AKTIV</th>
          <th style="padding:8px 12px;">ALPHA</th>
          <th style="padding:8px 12px;">AKTIONEN</th>
        </tr>
      </thead>
      <tbody id="au-tbody">
        ${users.map((u) => userRowHtml(u)).join('')}
      </tbody>
    </table>
  `;

  const tbody = document.getElementById('au-tbody');
  if (tbody) tbody.addEventListener('click', handleTableClick);
}

function userRowHtml(user: AdminUser): string {
  const username   = escapeHtml(user.profile?.username ?? '[kein Profil]');
  const createdAt  = new Date(user.createdAt).toLocaleDateString('de-DE');
  const lastSeen   = user.profile?.lastSeenAt
    ? new Date(user.profile.lastSeenAt).toLocaleDateString('de-DE')
    : '–';
  const isDeleted  = user.deletedAt !== null;

  let statusLabel: string;
  let statusColor: string;
  if (isDeleted) {
    statusLabel = 'GELÖSCHT';
    statusColor = 'var(--text-muted)';
  } else if (user.isBanned) {
    statusLabel = 'GESPERRT';
    statusColor = 'var(--accent-red)';
  } else if (user.isVerified) {
    statusLabel = 'VERIFIZIERT';
    statusColor = 'var(--accent-green)';
  } else {
    statusLabel = 'AUSSTEHEND';
    statusColor = 'var(--accent-gold)';
  }

  const rowStyle = isDeleted ? 'opacity:0.5;' : '';

  return `
    <tr data-user-id="${user.id}" style="border-bottom:1px solid #1a1a22;${rowStyle}">
      <td style="padding:8px 12px;color:var(--text-primary);">${username}</td>
      <td style="padding:8px 12px;color:${statusColor};">${statusLabel}</td>
      <td style="padding:8px 12px;color:var(--text-muted);">${createdAt}</td>
      <td style="padding:8px 12px;color:var(--text-muted);">${lastSeen}</td>
      <td style="padding:8px 12px;">
        ${alphaBadgeCellHtml(user)}
      </td>
      <td style="padding:8px 12px;">
        ${actionButtonsHtml(user)}
      </td>
    </tr>
  `;
}

// Zeigt, OB und WIE das Alpha-Tester-Badge vergeben wurde (NT-ALPHA-BADGE-AUTO-01, Teil C) —
// bzw. bietet die manuelle Vergabe an, solange der Spieler es noch nicht hat.
function alphaBadgeCellHtml(user: AdminUser): string {
  if (!user.profile) return '<span style="color:var(--text-muted);">–</span>';

  if (user.profile.isAlphaTester) {
    const method = user.profile.alphaTesterGrantMethod;
    const label = method === 'ADMIN_GRANTED' ? 'ADMIN' : method === 'AUTO_LEVEL' ? 'AUTO' : 'ALT';
    const color =
      method === 'ADMIN_GRANTED' ? 'var(--accent-purple)' :
      method === 'AUTO_LEVEL'    ? 'var(--accent-gold)' :
      'var(--text-muted)';
    return `<span style="color:${color};font-size:11px;">JA (${label})</span>`;
  }

  if (user.deletedAt !== null) return '<span style="color:var(--text-muted);">–</span>';

  return actionBtn('grant-alpha-badge', user.id, 'VERGEBEN', 'var(--accent-gold)');
}

function actionButtonsHtml(user: AdminUser): string {
  if (user.deletedAt !== null) {
    return '<span style="color:var(--text-muted);">–</span>';
  }

  if (user.isBanned) {
    return actionBtn('unban', user.id, 'ENTSPERREN', 'var(--accent-green)');
  }

  if (user.isVerified) {
    // Admin-Accounts nicht sperrbar
    if (user.role === 'ADMIN') {
      return '<span style="color:var(--text-muted);">ADMIN</span>';
    }
    return [
      actionBtn('reset-link', user.id, 'RESET-LINK', 'var(--accent-purple)'),
      actionBtn('ban', user.id, 'SPERREN', 'var(--accent-red)'),
    ].join('');
  }

  // Nicht verifiziert: Verifikations-Optionen
  return [
    actionBtn('resend',       user.id, 'LINK SENDEN',        'var(--accent-blue)'),
    actionBtn('generate',     user.id, 'LINK KOPIEREN',      'var(--accent-purple)'),
    actionBtn('force-verify', user.id, 'MANUELL VERIFIZIEREN','var(--accent-gold)'),
  ].join('');
}

function actionBtn(action: string, userId: string, label: string, color: string): string {
  return `
    <button
      data-au-action="${action}"
      data-au-user-id="${userId}"
      style="border:1px solid ${color};color:${color};background:transparent;
             padding:3px 8px;cursor:pointer;font-family:'Share Tech Mono',monospace;
             font-size:11px;margin-right:4px;margin-bottom:2px;white-space:nowrap;">
      ${label}
    </button>
  `;
}

// ─── Paginierung ──────────────────────────────────────────────────────────────

function renderPagination(meta: UsersMeta): void {
  const paginationEl = document.getElementById('au-pagination');
  if (!paginationEl) return;

  if (meta.pages <= 1) {
    paginationEl.innerHTML = '';
    return;
  }

  const btnStyle = (active: boolean): string =>
    `border:1px solid ${active ? 'var(--accent-green)' : 'var(--border)'};
     color:${active ? 'var(--accent-green)' : 'var(--text-muted)'};
     background:transparent;padding:4px 10px;cursor:pointer;
     font-family:'Share Tech Mono',monospace;font-size:12px;`;

  let html = `<span style="color:var(--text-muted);font-size:12px;margin-right:4px;">
    SEITE ${String(meta.page)} / ${String(meta.pages)}
  </span>`;

  if (meta.page > 1) {
    html += `<button data-au-page="${String(meta.page - 1)}" style="${btnStyle(false)}">◀ ZURÜCK</button>`;
  }
  if (meta.page < meta.pages) {
    html += `<button data-au-page="${String(meta.page + 1)}" style="${btnStyle(false)}">WEITER ▶</button>`;
  }

  paginationEl.innerHTML = html;
  paginationEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-au-page]');
    if (!btn) return;
    const page = parseInt(btn.getAttribute('data-au-page') ?? '1', 10);
    currentPage = page;
    void loadUsers();
  }, { once: true });
}

// ─── Aktionen (Table-Click-Delegation) ───────────────────────────────────────

function handleTableClick(e: Event): void {
  const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-au-action]');
  if (!btn) return;

  const action = btn.getAttribute('data-au-action');
  const userId = btn.getAttribute('data-au-user-id');
  if (!action || !userId) return;

  switch (action) {
    case 'resend':
      void handleResend(userId);
      break;
    case 'generate':
      void handleGenerateToken(userId);
      break;
    case 'force-verify':
      showConfirmModal(
        'User ohne E-Mail-Verifikation manuell als verifiziert markieren?',
        () => void handleForceVerify(userId),
      );
      break;
    case 'reset-link':
      void handleGenerateResetToken(userId);
      break;
    case 'ban':
      showConfirmModal(
        'Diesen Account sperren? Der User kann sich nicht mehr einloggen.',
        () => void handleBan(userId),
      );
      break;
    case 'unban':
      showConfirmModal(
        'Sperre für diesen Account aufheben?',
        () => void handleUnban(userId),
      );
      break;
    case 'grant-alpha-badge':
      showConfirmModal(
        'Alpha-Tester-Badge für diesen Account manuell vergeben?',
        () => void handleGrantAlphaBadge(userId),
      );
      break;
    default:
      break;
  }
}

// ─── API-Aktionen ─────────────────────────────────────────────────────────────

async function handleResend(userId: string, opts?: { refreshStale?: boolean }): Promise<void> {
  try {
    await api.post<{ message: string }>(`/admin/users/${userId}/resend-verification`, {});
    showToast('Verifikations-Mail wurde erneut versendet.', 'success');
    void loadUsers();
    if (opts?.refreshStale) void loadStaleUnverified();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Mail-Versand.', 'error');
  }
}

async function handleGenerateToken(userId: string): Promise<void> {
  try {
    const result = await api.post<{ verificationLink: string }>(
      `/admin/users/${userId}/generate-token`,
      {},
    );
    await navigator.clipboard.writeText(result.verificationLink);
    showToast('Verifikations-Link in die Zwischenablage kopiert.', 'success');
    void loadUsers();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Generieren des Links.', 'error');
  }
}

async function handleForceVerify(userId: string): Promise<void> {
  try {
    await api.post<{ message: string }>(`/admin/users/${userId}/force-verify`, {});
    showToast('User manuell verifiziert.', 'success');
    void loadUsers();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Verifizieren.', 'error');
  }
}

async function handleGenerateResetToken(userId: string): Promise<void> {
  try {
    const result = await api.post<{ resetLink: string }>(
      `/admin/users/${userId}/generate-reset-token`,
      {},
    );
    await navigator.clipboard.writeText(result.resetLink);
    showToast('Passwort-Reset-Link in die Zwischenablage kopiert.', 'success');
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Generieren des Reset-Links.', 'error');
  }
}

async function handleBan(userId: string): Promise<void> {
  try {
    await api.post<{ message: string }>(`/admin/users/${userId}/ban`, {});
    showToast('Account gesperrt.', 'success');
    void loadUsers();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Sperren.', 'error');
  }
}

async function handleUnban(userId: string): Promise<void> {
  try {
    await api.post<{ message: string }>(`/admin/users/${userId}/unban`, {});
    showToast('Sperre aufgehoben.', 'success');
    void loadUsers();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Entsperren.', 'error');
  }
}

async function handleGrantAlphaBadge(userId: string): Promise<void> {
  try {
    await api.post<{ message: string }>(`/admin/users/${userId}/grant-alpha-badge`, {});
    showToast('Alpha-Tester-Badge vergeben.', 'success');
    void loadUsers();
  } catch (err) {
    showToast(err instanceof ApiError ? err.message : 'Fehler beim Vergeben.', 'error');
  }
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function showConfirmModal(message: string, onConfirm: () => void): void {
  const overlay = document.getElementById('au-modal-overlay');
  const textEl  = document.getElementById('au-modal-text');
  if (!overlay || !textEl) return;

  textEl.textContent = message;
  pendingConfirmCallback = onConfirm;
  // Klasse erst hier setzen (nicht im Markup) — siehe Kommentar an der Template-Stelle oben.
  overlay.classList.add('modal-backdrop');
  overlay.style.display = 'flex';
}

function closeModal(): void {
  const overlay = document.getElementById('au-modal-overlay');
  if (overlay) {
    overlay.style.display = 'none';
    overlay.classList.remove('modal-backdrop');
  }
  pendingConfirmCallback = null;
}

// ─── Toast ────────────────────────────────────────────────────────────────────

function showToast(message: string, type: 'success' | 'error'): void {
  const toast = document.getElementById('au-toast');
  if (!toast) return;

  if (toastTimer !== null) clearTimeout(toastTimer);

  const color     = type === 'success' ? 'var(--accent-green)' : 'var(--accent-red)';
  const borderColor = type === 'success' ? 'var(--accent-green)' : 'var(--accent-red)';

  toast.style.background   = 'var(--bg-surface)';
  toast.style.color         = color;
  toast.style.borderColor   = borderColor;
  toast.textContent         = message;
  toast.hidden              = false;

  toastTimer = setTimeout(() => {
    toast.hidden = true;
  }, calcNoticeDisplayMs(message, type === 'error'));
}

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

function escapeAttr(str: string): string {
  return str.replace(/"/g, '&quot;');
}
